import React, { useRef, useState, useEffect } from "react";
import {
  Building,
  Plus,
  X,
  Upload,
  ChevronRight,
  ChevronLeft,
  CheckCircle2,
  MapPin,
  Trash2,
  Edit3,
  AlertCircle,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Map, AdvancedMarker, useMap, useMapsLibrary, ControlPosition } from "@vis.gl/react-google-maps";
import { cn } from "../../../../lib/utils";
import { Constants } from "../../../../utils/constants";
import { buildingAddressService } from "../../../../services/buildingAddressService";
import { spaceManagementService } from "../../../../services/host-apiServices/spaceManagementService";
import toastUtils from "../../../../utils/toast";
import { showApiMessage } from "../../../../utils/messageHandler";
import { getUserTimezone } from "../../../../utils/timezoneUtils";
import { getUserCountryCoordinates } from "../../../../utils/countryCoordinatesMap";
import { Button } from "../../../../shadcn/ui/button";
import { Input } from "../../../../shadcn/ui/input";
import { Label } from "../../../../shadcn/ui/label";
import TimezoneSelect from "../../../../components/TimezoneSelect";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../../../shadcn/ui/dialog";
import HSimpleFloorPlan from "../../../../hcomponents/HSimpleFloorPlan";
import GooglePlacesSearchInput from "./GooglePlacesSearchInput";
import MapErrorBoundary from "../../../../components/MapErrorBoundary";
import logger from "../../../../utils/logger";

const InlineBuildingAddressForm = ({ onAddressCreated, triggerButton }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [imageUploading, setImageUploading] = useState(false);
  const [floors, setFloors] = useState([]);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorIndex, setEditorIndex] = useState(null);
  const [editorInitialLayout, setEditorInitialLayout] = useState([]);
  const [placePicked, setPlacePicked] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [mapError, setMapError] = useState(false);
  const [initialCoordinates, setInitialCoordinates] = useState(null);
  const floorPlanRef = useRef(null);

  // Get user's country coordinates on mount
  useEffect(() => {
    (async () => {
      const userCoords = await getUserCountryCoordinates();
      if (userCoords) {
        setInitialCoordinates({
          latitude: userCoords.latitude,
          longitude: userCoords.longitude,
        });
      }
    })();
  }, []);

  const [panTarget, setPanTarget] = useState(null);
  const geocodingLib = useMapsLibrary("geocoding");

  /**
   * Internal component to control the map's camera programmatically
   * without creating a "controlled component" lock.
   */
  const MapController = ({ target }) => {
    const map = useMap();
    useEffect(() => {
      if (!map || !target) return;
      map.panTo({ lat: Number(target.latitude), lng: Number(target.longitude) });
      const currentZoom = map.getZoom() || 0;
      const targetZoom = target.zoom || 17;
      if (currentZoom < targetZoom) {
        map.setZoom(targetZoom);
      }
    }, [map, target]);
    return null;
  };

  const [formData, setFormData] = useState({
    buildingName: "",
    streetAddress: "",
    city: "",
    region: "",
    country: "",
    postalCode: "",
    digitalAddress: "",
    latitude: null,
    longitude: null,
    images: [],
    timezone: getUserTimezone(),
  });

  // Reset wizard when dialog opens and set initial coordinates
  React.useEffect(() => {
    if (isOpen) {
      setCurrentStep(1);
      setPlacePicked(false);
      (async () => {
        const userCoords = await getUserCountryCoordinates();
        if (userCoords) {
          setFormData(prev => ({
            ...prev,
            latitude: userCoords.latitude,
            longitude: userCoords.longitude,
          }));
          setInitialCoordinates({
            latitude: userCoords.latitude,
            longitude: userCoords.longitude,
          });
        }
      })();
    }
  }, [isOpen]);

  const resetForm = async () => {
    const userCoords = await getUserCountryCoordinates();
    setFormData({
      buildingName: "",
      streetAddress: "",
      city: "",
      region: "",
      country: "",
      postalCode: "",
      digitalAddress: "",
      latitude: userCoords?.latitude ?? null,
      longitude: userCoords?.longitude ?? null,
      images: [],
      timezone: getUserTimezone(),
    });
    setFloors([]);
    setPlacePicked(false);
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  /* ── Google Maps pick handler ── */
  const handlePlaceSelected = parsed => {
    setFormData(prev => ({
      ...prev,
      buildingName: parsed.buildingName || prev.buildingName,
      streetAddress: parsed.streetAddress || prev.streetAddress,
      city: parsed.city || prev.city,
      region: parsed.region || prev.region,
      country: parsed.country || prev.country,
      postalCode: parsed.postalCode || prev.postalCode,
      digitalAddress: parsed.digitalAddress || parsed.postalCode || prev.digitalAddress,
      timezone: parsed.timezone || prev.timezone,
      latitude: parsed.latitude ?? prev.latitude,
      longitude: parsed.longitude ?? prev.longitude,
    }));
    setPlacePicked(true);
    setPanTarget({ latitude: parsed.latitude, longitude: parsed.longitude });
  };

  // Prevent dialog from closing when clicking on Google Places autocomplete suggestions
  const handlePointerDownOutside = event => {
    const target = event.target;
    if (
      target?.closest?.(".pac-container") ||
      target?.closest?.(".pac-item") ||
      target?.classList?.contains("pac-container") ||
      target?.classList?.contains("pac-item") ||
      target?.closest?.("[data-pac-container]")
    ) {
      event.preventDefault();
    }
  };

  const handleInteractOutside = event => {
    const target = event.target;
    if (
      target?.closest?.(".pac-container") ||
      target?.closest?.(".pac-item") ||
      target?.classList?.contains("pac-container") ||
      target?.classList?.contains("pac-item") ||
      target?.closest?.("[data-pac-container]")
    ) {
      event.preventDefault();
    }
  };

  const handleFocusOutside = event => {
    const target = event.target;
    if (
      target?.closest?.(".pac-container") ||
      target?.closest?.(".pac-item") ||
      target?.classList?.contains("pac-container") ||
      target?.classList?.contains("pac-item") ||
      target?.closest?.("[data-pac-container]")
    ) {
      event.preventDefault();
    }
  };

  const handleImageUpload = async files => {
    try {
      setImageUploading(true);
      const validFiles = Array.from(files).filter(
        file => file.type.startsWith("image/") && file.size <= 5 * 1024 * 1024
      );

      if (validFiles.length === 0) {
        toastUtils.error("Please select valid image files (max 5MB each)");
        return;
      }

      const uploadResponse = await spaceManagementService.uploadMedia(validFiles);

      if (uploadResponse.status === 200 || uploadResponse.status === 201) {
        const newImageUrls = uploadResponse.data;
        setFormData(prev => ({
          ...prev,
          images: [...prev.images, ...newImageUrls],
        }));
        showApiMessage(uploadResponse, "upload images");
      } else {
        throw new Error("Upload failed");
      }
    } catch (error) {
      logger.error("Error uploading images:", error);
      showApiMessage(error, "upload images");
    } finally {
      setImageUploading(false);
    }
  };

  const removeImage = index => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index),
    }));
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);

      if (!formData.buildingName.trim()) {
        toastUtils.error("Building name is required");
        return;
      }
      if (!formData.streetAddress.trim()) {
        toastUtils.error("Street address is required");
        return;
      }
      if (!formData.city.trim()) {
        toastUtils.error("City is required");
        return;
      }
      if (!formData.region.trim()) {
        toastUtils.error("Region is required");
        return;
      }
      if (!formData.postalCode.trim()) {
        toastUtils.error("Postal code is required");
        return;
      }

      const payload = {
        ...formData,
        latitude: parseFloat(formData.latitude),
        longitude: parseFloat(formData.longitude),
        timezone: formData.timezone || getUserTimezone(),
        floors: floors.map(f => ({
          floorNumber: Number(f.floorNumber) || 0,
          label: f.label || "",
          floorPlan: {
            layout: Array.isArray(f.floorPlan?.layout) ? f.floorPlan.layout : [],
          },
        })),
      };

      const response = await buildingAddressService.createBuildingAddress(payload);

      if (response.status === 200 || response.status === 201) {
        showApiMessage(response, "create building address");
        const newAddress = response.data;

        if (onAddressCreated) {
          onAddressCreated(newAddress);
        }

        await resetForm();
        setIsOpen(false);
      } else {
        showApiMessage(response, "create building address");
      }
    } catch (error) {
      logger.error("Error creating building address:", error);
      showApiMessage(error, "create building address");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async () => {
    await resetForm();
    setCurrentStep(1);
    setMapError(false);
    setIsOpen(false);
  };

  const handleMapError = () => {
    setMapError(true);
  };

  const handleMapClick = event => {
    const lat = event.detail.latLng?.lat;
    const lng = event.detail.latLng?.lng;
    if (lat != null && lng != null) {
      setFormData(prev => ({ ...prev, latitude: lat, longitude: lng }));
      setPanTarget({ latitude: lat, longitude: lng, zoom: 17, timestamp: Date.now() });
      setPlacePicked(true);

      // Try reverse geocoding to fill address details
      if (geocodingLib) {
        const geocoder = new geocodingLib.Geocoder();
        geocoder.geocode({ location: { lat, lng } }, (results, status) => {
          if (status === "OK" && results[0]) {
            const place = results[0];
            const components = {};
            (place.address_components || []).forEach(c => {
              (c.types || []).forEach(t => {
                if (!components[t]) components[t] = c.long_name;
              });
            });

            const countryComp = (place.address_components || []).find(
              c => Array.isArray(c.types) && c.types.includes("country")
            );

            setFormData(prev => ({
              ...prev,
              buildingName:
                components.premise ||
                components.subpremise ||
                components.neighborhood ||
                components.sublocality_level_1 ||
                components.sublocality ||
                components.route ||
                components.locality ||
                prev.buildingName,
              streetAddress: place.formatted_address?.split(",")[0] || prev.streetAddress,
              city: components.locality || components.administrative_area_level_2 || prev.city,
              region: components.administrative_area_level_1 || prev.region,
              country: components.country || countryComp?.long_name || prev.country,
              postalCode: components.postal_code || "00000",
            }));
          }
        });
      }
    }
  };

  const nextStep = () => setCurrentStep(prev => Math.min(3, prev + 1));
  const prevStep = () => setCurrentStep(prev => Math.max(1, prev - 1));

  // Marker pin content (AdvancedMarker handles positioning)
  const MapMarkerPin = () => (
    <div className="relative">
      <div className="absolute -translate-x-1/2 -translate-y-full">
        <MapPin className="w-8 h-8 text-red-600 fill-red-100 drop-shadow-lg" />
      </div>
    </div>
  );

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        {triggerButton || (
          <Button variant="outline" size="icon" type="button">
            <Plus className="w-4 h-4" />
          </Button>
        )}
      </DialogTrigger>

      <DialogContent
        className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto"
        onPointerDownOutside={handlePointerDownOutside}
        onInteractOutside={handleInteractOutside}
        onFocusOutside={handleFocusOutside}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Building className="w-5 h-5" />
            Create New Building Address
          </DialogTitle>
          <DialogDescription>
            Search for your building on Google Maps to auto-fill the details, then review and save.
          </DialogDescription>
        </DialogHeader>

        <div className="py-2">
          {/* Step Progress Indicator */}
          <div className="flex items-center justify-between mb-8 px-2">
            {[
              { id: 1, label: "Location" },
              { id: 2, label: "Details" },
              { id: 3, label: "Floors" },
            ].map((s, i) => (
              <React.Fragment key={s.id}>
                <div className="flex flex-col items-center gap-2 relative z-10">
                  <div
                    className={cn(
                      "w-10 h-10 rounded-full flex items-center justify-center border-2 transition-all duration-300",
                      currentStep === s.id
                        ? "bg-blue-600 border-blue-600 text-white shadow-md scale-110"
                        : currentStep > s.id
                          ? "bg-green-500 border-green-500 text-white"
                          : "bg-white border-gray-200 text-gray-400"
                    )}
                  >
                    {currentStep > s.id ? <CheckCircle2 className="w-6 h-6" /> : <span>{s.id}</span>}
                  </div>
                  <span
                    className={cn(
                      "text-xs font-medium transition-colors",
                      currentStep === s.id ? "text-blue-600" : "text-gray-400"
                    )}
                  >
                    {s.label}
                  </span>
                </div>
                {i < 2 && (
                  <div className="flex-1 h-0.5 bg-gray-100 mx-4 -mt-6">
                    <motion.div
                      className="h-full bg-blue-600"
                      initial={{ width: "0%" }}
                      animate={{ width: currentStep > s.id ? "100%" : "0%" }}
                    />
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>

          <AnimatePresence mode="wait">
            {currentStep === 1 && (
              <motion.div
                key="step1"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="space-y-4"
              >
                <div className="relative h-[300px] w-full rounded-2xl overflow-hidden border border-gray-200 shadow-xl bg-gray-50">
                  {!mapError ? (
                    <MapErrorBoundary onError={handleMapError}>
                      <Map
                        mapId="DEMO_MAP_ID"
                        defaultCenter={{
                          lat: Number(formData.latitude) || 0,
                          lng: Number(formData.longitude) || 0,
                        }}
                        defaultZoom={17}
                        onClick={handleMapClick}
                        disableDefaultUI={true}
                        zoomControl={true}
                        zoomControlOptions={{
                          position: ControlPosition.RIGHT_CENTER,
                        }}
                        styles={[{ featureType: "poi", elementType: "labels", stylers: [{ visibility: "off" }] }]}
                        style={{ width: "100%", height: "100%" }}
                      >
                        <MapController target={panTarget} />
                        {placePicked && (
                          <AdvancedMarker position={{ lat: formData.latitude, lng: formData.longitude }}>
                            <MapMarkerPin />
                          </AdvancedMarker>
                        )}
                      </Map>
                    </MapErrorBoundary>
                  ) : (
                    <div className="absolute inset-0 bg-gray-50 flex items-center justify-center p-6 text-center">
                      <div className="max-w-xs animate-in fade-in zoom-in duration-500">
                        <div className="w-12 h-12 bg-amber-50 rounded-full flex items-center justify-center mx-auto mb-4">
                          <AlertCircle className="w-6 h-6 text-amber-600" />
                        </div>
                        <h3 className="text-base font-bold text-gray-900 mb-1">Map Loading Failed</h3>
                        <p className="text-sm text-gray-500 mb-4">
                          We couldn't initialize Google Maps. You can still enter the address manually.
                        </p>
                        <div className="flex flex-col gap-2">
                          <button
                            type="button"
                            onClick={nextStep}
                            className="inline-flex items-center justify-center px-4 py-2 text-sm font-semibold text-white bg-blue-600 rounded-xl hover:bg-blue-700 transition-all"
                          >
                            Enter address manually
                          </button>
                          <button
                            type="button"
                            onClick={() => setMapError(false)}
                            className="text-xs font-medium text-gray-500 hover:text-blue-600 underline"
                          >
                            Try reloading map
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Absolute positioned search - Top center */}
                  {!mapError && (
                    <div className="absolute top-4 inset-x-4 z-20">
                      <div className="max-w-md mx-auto">
                        <GooglePlacesSearchInput
                          onPlaceSelected={handlePlaceSelected}
                          placeholder="Search for your building..."
                          showSelectedBadge={false}
                          className="shadow-2xl"
                        />
                      </div>
                    </div>
                  )}

                  {!mapError && !placePicked && (
                    <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-10 pointer-events-none whitespace-nowrap">
                      <div className="bg-white/90 backdrop-blur-sm px-4 py-2 rounded-xl shadow-lg border border-gray-100 flex items-center gap-2">
                        <MapPin className="w-4 h-4 text-blue-600" />
                        <span className="text-sm font-medium text-gray-700">Click on the map to pin your building</span>
                      </div>
                    </div>
                  )}

                  {placePicked && (
                    <div className="absolute bottom-4 left-4 right-16 sm:right-auto sm:max-w-md z-20">
                      <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="bg-white/95 backdrop-blur-sm p-4 rounded-xl shadow-xl border border-green-100 flex items-center gap-3"
                      >
                        <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                          <CheckCircle2 className="w-6 h-6 text-green-600" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-bold text-gray-900 truncate">
                            {formData.buildingName || "Location Selected"}
                          </p>
                          <p className="text-xs text-gray-500 truncate">{formData.streetAddress}</p>
                        </div>
                      </motion.div>
                    </div>
                  )}
                </div>
              </motion.div>
            )}

            {currentStep === 2 && (
              <motion.div
                key="step2"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-5"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="buildingName">
                      Building Name <span className="text-red-400">*</span>
                    </Label>
                    <Input
                      id="buildingName"
                      value={formData.buildingName}
                      onChange={e => handleInputChange("buildingName", e.target.value)}
                      placeholder="Downtown Business Center"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="streetAddress">
                      Street Address <span className="text-red-400">*</span>
                    </Label>
                    <Input
                      id="streetAddress"
                      value={formData.streetAddress}
                      onChange={e => handleInputChange("streetAddress", e.target.value)}
                      placeholder="123 Main Street"
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="city">
                      City <span className="text-red-400">*</span>
                    </Label>
                    <Input
                      id="city"
                      value={formData.city}
                      onChange={e => handleInputChange("city", e.target.value)}
                      placeholder="Nairobi"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="region">
                      Region / State <span className="text-red-400">*</span>
                    </Label>
                    <Input
                      id="region"
                      value={formData.region}
                      onChange={e => handleInputChange("region", e.target.value)}
                      placeholder="Nairobi County"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="country">Country</Label>
                    <Input
                      id="country"
                      value={formData.country}
                      onChange={e => handleInputChange("country", e.target.value)}
                      placeholder="Kenya"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="postalCode">
                      Postal Code <span className="text-red-400">*</span>
                    </Label>
                    <Input
                      id="postalCode"
                      value={formData.postalCode}
                      onChange={e => handleInputChange("postalCode", e.target.value)}
                      placeholder="00100"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="digitalAddress">Digital Address</Label>
                    <Input
                      id="digitalAddress"
                      value={formData.digitalAddress}
                      onChange={e => handleInputChange("digitalAddress", e.target.value)}
                      placeholder="Plus code or GPS address"
                    />
                  </div>
                </div>

                <div>
                  <TimezoneSelect
                    value={formData.timezone}
                    onChange={value => handleInputChange("timezone", value)}
                    label="Timezone"
                  />
                </div>

                <div>
                  <Label>Building Images (Optional)</Label>
                  <div className="mt-2 space-y-3">
                    <input
                      type="file"
                      multiple
                      accept="image/*"
                      onChange={e => handleImageUpload(e.target.files)}
                      className="hidden"
                      id="image-upload"
                      disabled={imageUploading}
                    />
                    <label
                      htmlFor="image-upload"
                      className="flex items-center gap-2 px-4 py-2 border border-dashed border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors w-full justify-center"
                    >
                      <Upload className="w-4 h-4 text-gray-400" />
                      <span className="text-sm font-medium text-gray-600">
                        {imageUploading ? "Uploading..." : "Click to upload building images"}
                      </span>
                    </label>

                    {formData.images.length > 0 && (
                      <div className="grid grid-cols-4 gap-2">
                        {formData.images.map((imageUrl, index) => (
                          <div key={index} className="relative group aspect-square">
                            <img
                              src={imageUrl}
                              alt={`Building ${index + 1}`}
                              className="w-full h-full object-cover rounded-lg border border-gray-200"
                            />
                            <button
                              type="button"
                              onClick={() => removeImage(index)}
                              className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center shadow-sm opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>
            )}

            {currentStep === 3 && (
              <motion.div
                key="step3"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-6"
              >
                <div className="rounded-xl border border-blue-100 bg-blue-50/50 p-4 flex items-center justify-between">
                  <div>
                    <Label className="text-sm font-semibold text-blue-900">Manage Floors</Label>
                    <p className="text-xs text-blue-700">Add floors and define their layouts</p>
                  </div>
                  <Button
                    type="button"
                    variant="spaciadefault"
                    size="sm"
                    onClick={() => {
                      const index = floors.length;
                      setFloors(prev => [
                        ...prev,
                        {
                          floorNumber: index,
                          label: "",
                          floorPlan: { layout: [] },
                        },
                      ]);
                    }}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Floor
                  </Button>
                </div>

                {floors.length === 0 ? (
                  <div className="text-center py-12 border-2 border-dashed border-gray-100 rounded-xl">
                    <Building className="w-12 h-12 text-gray-200 mx-auto mb-3" />
                    <p className="text-sm text-gray-400">No floors added yet. You can skip this step.</p>
                  </div>
                ) : (
                  <div className="space-y-3 max-h-[400px] overflow-y-auto pr-2">
                    {floors.map((floor, i) => (
                      <div key={i} className="border border-gray-200 rounded-xl p-4 bg-white shadow-sm space-y-4">
                        <div className="flex items-start justify-between">
                          <div className="grid grid-cols-2 gap-4 flex-1">
                            <div className="space-y-1.5">
                              <Label className="text-xs">Floor No.</Label>
                              <Input
                                type="number"
                                value={floor.floorNumber}
                                onChange={e =>
                                  setFloors(prev =>
                                    prev.map((f, idx) => (idx === i ? { ...f, floorNumber: e.target.value } : f))
                                  )
                                }
                                className="h-9"
                              />
                            </div>
                            <div className="space-y-1.5">
                              <Label className="text-xs">Label</Label>
                              <Input
                                value={floor.label}
                                onChange={e =>
                                  setFloors(prev =>
                                    prev.map((f, idx) => (idx === i ? { ...f, label: e.target.value } : f))
                                  )
                                }
                                placeholder="Ground, 1st..."
                                className="h-9"
                              />
                            </div>
                          </div>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="text-red-400 hover:text-red-500 hover:bg-red-50"
                            onClick={() => setFloors(prev => prev.filter((_, idx) => idx !== i))}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                        <div className="flex items-center justify-between pt-2 border-t border-gray-50">
                          <span className="text-xs text-gray-500 bg-gray-50 px-2 py-1 rounded">
                            {Array.isArray(floor.floorPlan?.layout) && floor.floorPlan.layout.length > 0
                              ? `${floor.floorPlan.layout.length} elements in layout`
                              : "No layout defined"}
                          </span>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setEditorIndex(i);
                              const layout = Array.isArray(floor.floorPlan?.layout) ? floor.floorPlan.layout : [];
                              setEditorInitialLayout(layout);
                              setEditorOpen(true);
                            }}
                          >
                            <Edit3 className="w-3.5 h-3.5 mr-2" />
                            Edit Floor Plan
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Improved Footer Navigation */}
        <div className="flex items-center justify-between pt-6 border-t mt-4">
          <Button variant="ghost" onClick={handleCancel} disabled={loading} className="text-gray-500">
            Cancel
          </Button>

          <div className="flex items-center gap-2">
            {currentStep > 1 && (
              <Button variant="outline" onClick={prevStep} disabled={loading}>
                <ChevronLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
            )}

            {currentStep < 3 ? (
              <Button
                variant="spaciadefault"
                onClick={nextStep}
                disabled={currentStep === 1 && !placePicked && !mapError}
                className="min-w-[100px]"
              >
                Next
                <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            ) : (
              <Button onClick={handleSubmit} disabled={loading || imageUploading} className="min-w-[120px]">
                {loading ? (
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Saving...
                  </div>
                ) : (
                  "Create Address"
                )}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>

      {/* Nested floor plan editor dialog */}
      <Dialog open={editorOpen} onOpenChange={setEditorOpen}>
        <DialogContent className="sm:max-w-[960px] w-full max-h-[90vh] overflow-hidden p-0">
          <div className="flex flex-col h-[80vh]">
            <div className="flex items-center justify-between px-4 py-3 border-b">
              <DialogTitle>Edit Floor Plan</DialogTitle>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setEditorOpen(false)} type="button">
                  Cancel
                </Button>
                <Button
                  variant="spaciadefault"
                  onClick={() => {
                    if (floorPlanRef.current && typeof floorPlanRef.current.getSaveData === "function") {
                      const data = floorPlanRef.current.getSaveData();
                      const newLayout = Array.isArray(data) ? data : Array.isArray(data?.layout) ? data.layout : [];
                      setFloors(prev =>
                        prev.map((f, idx) => (idx === editorIndex ? { ...f, floorPlan: { layout: newLayout } } : f))
                      );
                      setEditorOpen(false);
                    }
                  }}
                  type="button"
                >
                  Save
                </Button>
              </div>
            </div>
            <div className="flex-1 min-h-0">
              <HSimpleFloorPlan
                ref={floorPlanRef}
                initialLayout={editorInitialLayout}
                onSave={() => {}}
                className="w-full h-full"
                useIcons={true}
                useColors={false}
              />
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
};

export default InlineBuildingAddressForm;
